import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useState } from "react";
import { toast } from "sonner";

export default function Cart() {
  const { items, remove, subtotal, clear, updateArtwork } = useCart();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploadingLine, setUploadingLine] = useState(null);
  const nav = useNavigate();

  const uploadArtworkForLine = async (line_id, e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!user) { toast.error("Please sign in to upload artwork"); nav("/login"); return; }
    setUploadingLine(line_id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/artwork", fd);
      updateArtwork(line_id, data.id, data.original_filename);
      toast.success("Artwork uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploadingLine(null); }
  };

  const checkout = async () => {
    if (items.length === 0) return;
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/session", {
        origin_url: window.location.origin,
        items: items.map(i => ({
          product_id: i.product_id, quantity: i.quantity, options: i.options, notes: i.notes,
          artwork_file_id: i.artwork_file_id || null,
        })),
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed. Please sign in first.");
      if (e.response?.status === 401) nav("/login");
    } finally { setLoading(false); }
  };

  return (
    <section className="max-w-[1200px] mx-auto px-6 md:px-12 py-16">
      <h1 className="font-serif text-5xl md:text-7xl">Your Cart</h1>
      {items.length === 0 ? (
        <div className="mt-12 border border-[#E5E5E5] p-12 text-center">
          <p className="text-[#555]">Your cart is empty.</p>
          <Link to="/shop" data-testid="cart-empty-shop" className="mt-6 inline-block bg-[#0a0a0a] text-white px-8 py-4 text-xs tracking-[0.25em] uppercase font-bold hover:bg-[#C5A059] hover:text-[#0a0a0a]">Continue Shopping</Link>
        </div>
      ) : (
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="md:col-span-2 space-y-4">
            {items.map((it, i) => (
              <div key={it.line_id} className="flex gap-6 p-4 border border-[#E5E5E5]" data-testid={`cart-item-${i}`}>
                <img src={it.image_url} alt={it.name} className="w-24 h-24 object-cover" />
                <div className="flex-1">
                  <h3 className="font-serif text-xl">{it.name}</h3>
                  <p className="font-mono text-xs text-[#888] mt-1">Qty {it.quantity} · {Object.entries(it.options || {}).map(([k,v]) => `${k}:${v}`).join(" · ")}</p>

                  {/* Artwork attach / replace */}
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    {it.artwork_file_id ? (
                      <>
                        <span className="text-xs text-[#1F4D2A] font-mono" data-testid={`cart-artwork-attached-${i}`}>✓ Artwork: {it.artwork_filename || "attached"}</span>
                        <label className="text-[10px] font-mono tracking-[0.2em] uppercase underline cursor-pointer text-[#555] hover:text-[#0a0a0a]" data-testid={`cart-artwork-replace-${i}`}>
                          {uploadingLine === it.line_id ? "Uploading..." : "Replace"}
                          <input type="file" hidden accept=".pdf,.png,.jpg,.jpeg,.svg,.ai,.eps,.psd,.zip,.tiff,.tif,.gif,.webp" onChange={e => uploadArtworkForLine(it.line_id, e)} disabled={uploadingLine === it.line_id} />
                        </label>
                      </>
                    ) : (
                      <label className="text-xs font-mono tracking-[0.25em] uppercase border border-[#0a0a0a] px-3 py-2 cursor-pointer hover:bg-[#FAFAFA]" data-testid={`cart-artwork-upload-${i}`}>
                        {uploadingLine === it.line_id ? "Uploading..." : "Attach Artwork"}
                        <input type="file" hidden accept=".pdf,.png,.jpg,.jpeg,.svg,.ai,.eps,.psd,.zip,.tiff,.tif,.gif,.webp" onChange={e => uploadArtworkForLine(it.line_id, e)} disabled={uploadingLine === it.line_id} />
                      </label>
                    )}
                    <span className="text-[10px] text-[#888]">Optional — you can also upload after checkout</span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-mono">${(it.unit_price * it.quantity).toFixed(2)}</p>
                  <button data-testid={`cart-remove-${i}`} onClick={() => remove(it.line_id)} className="text-xs text-[#9A1F1F] mt-2">Remove</button>
                </div>
              </div>
            ))}
          </div>
          <div className="border border-[#E5E5E5] p-6 h-fit">
            <h3 className="font-serif text-2xl mb-4">Summary</h3>
            <div className="flex justify-between font-mono py-2 border-b border-[#E5E5E5]"><span>Subtotal</span><span data-testid="cart-subtotal">${subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between font-mono py-2 text-[#555] text-sm"><span>Shipping</span><span>Calculated at checkout</span></div>
            <button data-testid="cart-checkout" onClick={checkout} disabled={loading} className="mt-6 w-full bg-[#0a0a0a] text-white hover:bg-[#C5A059] hover:text-[#0a0a0a] transition-colors px-8 py-4 text-xs tracking-[0.25em] uppercase font-bold">
              {loading ? "Processing..." : "Checkout"}
            </button>
            <button onClick={clear} className="mt-3 w-full text-xs text-[#555] underline">Clear cart</button>
          </div>
        </div>
      )}
    </section>
  );
}
